import { useState, useMemo } from "react";
import { useThemeMode } from "../../contexts/theme-mode";
import {
  Box,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Button,
  Tooltip,
  alpha,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import { getFields } from "./Fields";
import { renderDetailedItem } from "./DetailedItem";
import { ScheduleEntryStatus, ScheduleEntryStatusMap } from "./DetailedItem";
import { Schedule } from "@mui/icons-material";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import { canManagePaymentMethod } from "../../utils/roleHelpers";
import { ROLE_FRONT_DESK } from "../../utils/constants";
import { toast } from "react-hot-toast";
import {
  SchedulerAPI,
  formatExistingAppointmentStart,
  formatFloatingWallClock,
  schedulerBookingMinTime,
  schedulerBookingMaxTime,
  weekConfig,
} from "./config";
import {
  clampStep15,
  clampGapMinutes,
  formatDurationHuman,
  formatGapHuman,
} from "./durationFormat";
import {
  buildSegmentDurationOverrides,
  isLastBlockOfMultiStepService,
  sameSegment,
  segmentDraftKey,
  segmentMinutesFromEvent,
} from "./segmentDurationOverrides";
import {
  buildSegmentGapOverridesPayload,
  parseSegmentGapOverrides,
} from "./segmentGapOverrides";
import { getAppointmentMenuActions } from "./appointmentMenuActions";
import { colors } from "../../theme/colors";
import {
  CONTROL_RADIUS,
  dialogPaperSx,
  dialogTitleSx,
  useSchedulerSurface,
  dialogCloseIconSx,
  primaryBtnSx,
  secondaryBtnSx,
  fieldSx,
} from "./schedulerDialogStyles";
import AddDepositModal from "./AddDepositModal";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";

dayjs.extend(utc);
dayjs.extend(timezone);

const statusColors = {
  [ScheduleEntryStatus.Scheduled]: colors.gold,
  [ScheduleEntryStatus.InProgress]: "#ed6c02",
  [ScheduleEntryStatus.Finished]: "#2e7d32",
  [ScheduleEntryStatus.Canceled]: "#9e9e9e",
  [ScheduleEntryStatus.NoShow]: "#d32f2f",
};

const QUICK_DURATION_CHIPS = [
  15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 180, 210, 240,
];

const QUICK_DURATION_CHIPS_WITH_ZERO = [0, ...QUICK_DURATION_CHIPS];

const QUICK_GAP_CHIPS = [0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150];

const AppointmentView = ({
  event,
  close,
  appointmentIdToCopy,
  resources,
  events,
  calendarRef,
  handleDelete,
  handleCopyAppointment,
  onReschedule,
  onStartAppointment,
  onFinishAppointment,
  onMarkAsLate,
  onCancelAppointment,
  onNoShow,
  refreshData,
}) => {
  const surfaceRef = useSchedulerSurface();
  const { isDark } = useThemeMode();
  const ink = isDark ? "#f2f2f2" : colors.black;
  const { user } = useAuth();
  const canViewPaymentMethod = canManagePaymentMethod(user?.userRoles);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [rescheduleStart, setRescheduleStart] = useState(() =>
    event?.start ? dayjs(event.start) : dayjs()
  );
  const [editingSegment, setEditingSegment] = useState(null);
  const [editingGap, setEditingGap] = useState(null);
  const [draftMinutes, setDraftMinutes] = useState(15);
  const [draftGapMinutes, setDraftGapMinutes] = useState(0);
  const [savingDurations, setSavingDurations] = useState(false);
  const [savingGap, setSavingGap] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);

  if (appointmentIdToCopy) {
    close();
    return null;
  }

  const status = event?.appointment_status;
  const statusLabel = ScheduleEntryStatusMap[status] ?? "Unknown";
  const accentColor = statusColors[status] ?? colors.gold;
  const start = dayjs(event?.start);
  const end = dayjs(event?.end);
  const durationMins = Math.max(0, end.diff(start, "minute"));

  const mergedSegments = useMemo(() => {
    if (event?.appointmentId == null) return [];
    return events
      .filter(
        (e) =>
          e.appointmentId === event.appointmentId && e.user_id === event.user_id
      )
      .sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
  }, [event?.appointmentId, event?.user_id, events]);

  const segmentGapOverrides = useMemo(() => {
    const raw =
      event?.segment_gap_overrides ?? mergedSegments[0]?.segment_gap_overrides;
    return parseSegmentGapOverrides(raw);
  }, [event?.segment_gap_overrides, mergedSegments]);

  const canEditServiceDurations =
    user?.userRoles?.includes(ROLE_FRONT_DESK) &&
    event?.appointmentId != null &&
    (status === ScheduleEntryStatus.Scheduled ||
      status === ScheduleEntryStatus.InProgress);

  const canAddDeposit =
    canViewPaymentMethod &&
    event?.appointmentId != null &&
    (status === ScheduleEntryStatus.Scheduled ||
      status === ScheduleEntryStatus.InProgress);

  const menuActions = getAppointmentMenuActions({ status, canAddDeposit });

  const editingAllowsZero = Boolean(
    editingSegment &&
      isLastBlockOfMultiStepService(mergedSegments, editingSegment)
  );
  const durationMin = editingAllowsZero ? 0 : 15;

  const adjustDraft = (delta) => {
    setDraftMinutes((m) => clampStep15(m + delta, durationMin));
  };

  const setDraftPreset = (mins) => {
    setDraftMinutes(clampStep15(mins, durationMin));
  };

  const adjustGapDraft = (delta) => {
    setDraftGapMinutes((m) => clampGapMinutes(m + delta));
  };

  const setGapDraftPreset = (mins) => {
    setDraftGapMinutes(clampGapMinutes(mins));
  };

  const handleSaveDurations = async () => {
    if (!mergedSegments.length || !editingSegment) return;
    const primary = mergedSegments[0];
    const uniqueServices = primary.uniqueServices || event.uniqueServices;
    if (!uniqueServices?.length) {
      toast.error("Missing service list for this appointment.", {
        duration: 3000,
      });
      return;
    }

    const draftByKey = {};
    mergedSegments.forEach((s) => {
      const allowZero = isLastBlockOfMultiStepService(mergedSegments, s);
      const key = segmentDraftKey(s);
      draftByKey[key] = sameSegment(s, editingSegment)
        ? draftMinutes
        : segmentMinutesFromEvent(s, allowZero);
    });

    setSavingDurations(true);
    try {
      const startSource = primary.scheduleStart ?? primary.start;
      const formatted = formatExistingAppointmentStart(startSource);
      const overrides = buildSegmentDurationOverrides(
        mergedSegments,
        uniqueServices,
        draftByKey
      );

      const res = await baseAxios.put(SchedulerAPI.modify, {
        start: formatted,
        user_id: primary.user_id,
        location_id: primary.location?.location_id,
        client_id: primary.client?.client_id,
        services: uniqueServices,
        schedule_entry_id: primary.appointmentId,
        booking_source: primary.booking_source,
        segment_duration_overrides: overrides,
      });
      if (res.status === 200) {
        toast.success("Service lengths updated", { duration: 2500 });
        setEditingSegment(null);
        await refreshData?.();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not update service lengths";
      toast.error(msg, { duration: 3500 });
    } finally {
      setSavingDurations(false);
    }
  };

  const handleSaveGap = async () => {
    if (!mergedSegments.length || !editingGap) return;
    if (editingGap.serviceId == null) {
      toast.error("Could not determine which service this gap belongs to.", {
        duration: 3000,
      });
      return;
    }
    const primary = mergedSegments[0];
    const uniqueServices = primary.uniqueServices || event.uniqueServices;
    if (!uniqueServices?.length) {
      toast.error("Missing service list for this appointment.", {
        duration: 3000,
      });
      return;
    }

    setSavingGap(true);
    try {
      // Keep existing start wall-clock; do not use formatScheduleUpdateStart (shifts time)
      const startSource = primary.scheduleStart ?? primary.start;
      const formatted = formatExistingAppointmentStart(startSource);
      const segmentGapPayload = buildSegmentGapOverridesPayload(
        segmentGapOverrides,
        editingGap.serviceId,
        draftGapMinutes
      );

      const res = await baseAxios.put(SchedulerAPI.modify, {
        start: formatted,
        user_id: primary.user_id,
        location_id: primary.location?.location_id,
        client_id: primary.client?.client_id,
        services: uniqueServices,
        schedule_entry_id: primary.appointmentId,
        booking_source: primary.booking_source,
        segment_gap_overrides: segmentGapPayload,
      });
      if (res.status === 200) {
        toast.success("Processing gap updated", { duration: 2500 });
        setEditingGap(null);
        await refreshData?.();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not update processing gap";
      toast.error(msg, { duration: 3500 });
    } finally {
      setSavingGap(false);
    }
  };

  return (
    <Box
      ref={surfaceRef}
      sx={{
        display: "flex",
        minWidth: 320,
        maxWidth: 380,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Top bar — close + title + menu */}
        <Stack
          direction="row"
          alignItems="flex-start"
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            bgcolor: colors.black,
            color: colors.white,
          }}
          gap={1}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.3,
                fontSize: "16px !important",
                color: colors.white,
              }}
              noWrap
            >
              {event?.title || "Appointment"}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} mt={0.25}>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "12px",
                  lineHeight: 1,
                  fontWeight: 600,
                  bgcolor: colors.gold,
                  color: colors.black,
                  border: "none",
                  borderRadius: CONTROL_RADIUS,
                }}
              />
              {status === ScheduleEntryStatus.Finished && (
                <Chip
                  label="Checked-out"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "12px",
                    lineHeight: 1,
                    fontWeight: 700,
                    bgcolor: "#4ADE80",
                    color: colors.black,
                    border: "none",
                    borderRadius: CONTROL_RADIUS,
                  }}
                />
              )}
              {event?.is_late && (
                <Chip
                  label="Late"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "12px",
                    lineHeight: 1,
                    fontWeight: 600,
                    bgcolor: alpha("#ed6c02", 0.2),
                    color: "#ffb74d",
                    border: "none",
                    borderRadius: CONTROL_RADIUS,
                  }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" gap={0}>
            {confirmDelete ? (
              <Stack direction="row" gap={1} alignItems="center">
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0,
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderRadius: CONTROL_RADIUS,
                  }}
                  onClick={() => {
                    handleDelete(event.appointmentId);
                    setConfirmDelete(false);
                    close();
                  }}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    py: 0,
                    fontSize: "0.7rem",
                    textTransform: "none",
                    color: colors.gold,
                  }}
                  onClick={() => setConfirmDelete(false)}
                >
                  No
                </Button>
              </Stack>
            ) : (
              <Tooltip title="More">
                <IconButton
                  size="small"
                  sx={{ p: 0.25, color: colors.gold }}
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                >
                  <MoreHorizRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close">
              <IconButton
                size="small"
                sx={{ p: 0.25, color: colors.gold }}
                onClick={close}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Time row */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="caption" color="text.secondary" lineHeight={1}>
            {formatFloatingWallClock(start, false, "ddd, DD MMM YYYY")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              backgroundColor: "rgba(197, 160, 111, 0.25)",
              border: `1px dashed ${colors.gold}`,
              padding: "2px 6px",
              borderRadius: CONTROL_RADIUS,
              fontSize: "12px",
              color: ink,
            }}
          >
            {formatFloatingWallClock(start)} {"\u2013"}{" "}
            {formatFloatingWallClock(end)}
          </Typography>
          {durationMins > 0 && (
            <Typography
              variant="caption"
              sx={{ color: colors.goldHover, fontWeight: 600 }}
              lineHeight={1}
            >
              {formatDurationHuman(clampStep15(durationMins))}
            </Typography>
          )}
        </Stack>

        <Divider />

        {/* Body — details + actions */}
        {renderDetailedItem(
          getFields({ stylists: resources }),
          event,
          events,
          onStartAppointment,
          onFinishAppointment,
          onMarkAsLate,
          onCancelAppointment,
          onNoShow,
          mergedSegments.length > 0
            ? {
                segments: mergedSegments,
                segmentGapOverrides,
                canEdit: canEditServiceDurations,
                onEditSegment: (seg) => {
                  setEditingGap(null);
                  setEditingSegment(seg);
                  setDraftMinutes(
                    segmentMinutesFromEvent(
                      seg,
                      isLastBlockOfMultiStepService(mergedSegments, seg)
                    )
                  );
                },
                onEditGap: (gapItem) => {
                  setEditingSegment(null);
                  setEditingGap(gapItem);
                  setDraftGapMinutes(clampGapMinutes(gapItem.gapMinutes));
                },
              }
            : null,
          { canView: canViewPaymentMethod }
        )}
      </Box>

      {/* Overflow menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 150, borderRadius: 2 } } }}
      >
        {menuActions.includes("edit") && (
          <MenuItem
            dense
            onClick={() => {
              calendarRef.current?.scheduler?.triggerDialog(true, event);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              Edit
            </ListItemText>
          </MenuItem>
        )}
        {menuActions.includes("reschedule") && (
          <MenuItem
            dense
            onClick={() => {
              setRescheduleStart(event?.start ? dayjs(event.start) : dayjs());
              setRescheduleOpen(true);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Schedule fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              Reschedule
            </ListItemText>
          </MenuItem>
        )}
        {menuActions.includes("copy") && (
          <MenuItem
            dense
            onClick={() => {
              handleCopyAppointment(event.appointmentId);
              setMenuAnchor(null);
              close();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              Copy & Paste
            </ListItemText>
          </MenuItem>
        )}
        {menuActions.includes("deposit") && (
          <MenuItem
            dense
            onClick={() => {
              setDepositOpen(true);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <PaymentsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              Add Deposit
            </ListItemText>
          </MenuItem>
        )}
        {menuActions.length > 1 && <Divider />}
        {menuActions.includes("delete") && (
          <MenuItem
            dense
            onClick={() => {
              setConfirmDelete(true);
              setMenuAnchor(null);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>
              Delete
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={rescheduleOpen}
        onClose={() => !rescheduleSaving && setRescheduleOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }} color="inherit">
            Reschedule
          </Typography>
          <IconButton
            disabled={rescheduleSaving}
            onClick={() => setRescheduleOpen(false)}
            sx={dialogCloseIconSx}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: "14px" }}>New date & time</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                format="MMM D, h:mm A"
                ampm
                minutesStep={weekConfig.step}
                minTime={schedulerBookingMinTime()}
                maxTime={schedulerBookingMaxTime()}
                value={rescheduleStart}
                disabled={rescheduleSaving}
                onChange={(value) => {
                  if (value) setRescheduleStart(value);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: fieldSx,
                    helperText:
                      "Salon hours: 9:00 AM – 8:00 PM. Set AM/PM when changing the hour.",
                  },
                }}
              />
            </LocalizationProvider>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: CONTROL_RADIUS,
                bgcolor: "rgba(197, 160, 111, 0.15)",
                border: "1px solid",
                borderColor: colors.gold,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Rescheduling to
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
                {rescheduleStart.format("dddd, MMM D, YYYY · h:mm A")}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            disabled={rescheduleSaving}
            onClick={() => setRescheduleOpen(false)}
            sx={secondaryBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (rescheduleSaving) return;
              setRescheduleSaving(true);
              try {
                await onReschedule?.(event, rescheduleStart);
                setRescheduleOpen(false);
                close();
              } finally {
                setRescheduleSaving(false);
              }
            }}
            disabled={rescheduleSaving}
            startIcon={
              rescheduleSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={primaryBtnSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingSegment)}
        onClose={() => !savingDurations && setEditingSegment(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }} color="inherit">
            Edit duration
          </Typography>
          <IconButton
            size="small"
            disabled={savingDurations}
            onClick={() => setEditingSegment(null)}
            sx={dialogCloseIconSx}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingSegment ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {editingSegment.services?.[0]?.title ??
                    editingSegment.title ??
                    "Service"}
                </Typography>
                <Typography variant="body2">
                  {formatFloatingWallClock(
                    editingSegment.services?.[0]?.serviceStart ??
                      editingSegment.start,
                    true
                  )}{" "}
                  –{" "}
                  {formatFloatingWallClock(
                    editingSegment.services?.[0]?.serviceEnd ??
                      editingSegment.end,
                    true
                  )}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {editingAllowsZero
                  ? "Finishing can be 0 minutes. It still appears on the scheduler but does not book the stylist."
                  : "Length uses 15-minute steps. Appointment start time does not change; processing gap can be edited separately."}
              </Typography>
              <Stack alignItems="center" spacing={1.5}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}
                >
                  {formatDurationHuman(draftMinutes)}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <IconButton
                    size="medium"
                    aria-label="decrease duration"
                    disabled={savingDurations || draftMinutes <= durationMin}
                    onClick={() => adjustDraft(-15)}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <IconButton
                    size="medium"
                    aria-label="increase duration"
                    disabled={savingDurations || draftMinutes >= 480}
                    onClick={() => adjustDraft(15)}
                  >
                    <AddIcon />
                  </IconButton>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Quick picks
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                  justifyContent="center"
                >
                  {(editingAllowsZero
                    ? QUICK_DURATION_CHIPS_WITH_ZERO
                    : QUICK_DURATION_CHIPS
                  ).map((p) => (
                    <Chip
                      key={p}
                      label={formatDurationHuman(p)}
                      size="small"
                      variant={draftMinutes === p ? "filled" : "outlined"}
                      disabled={savingDurations}
                      onClick={() => setDraftPreset(p)}
                      sx={{
                        fontWeight: 600,
                        borderRadius: CONTROL_RADIUS,
                        ...(draftMinutes === p
                          ? {
                              bgcolor: colors.gold,
                              color: colors.black,
                            }
                          : {
                              borderColor: colors.gold,
                              color: ink,
                            }),
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button
            onClick={() => setEditingSegment(null)}
            disabled={savingDurations}
            sx={secondaryBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingDurations}
            onClick={handleSaveDurations}
            startIcon={
              savingDurations ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={primaryBtnSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingGap)}
        onClose={() => !savingGap && setEditingGap(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }} color="inherit">
            Edit gap
          </Typography>
          <IconButton
            size="small"
            disabled={savingGap}
            onClick={() => setEditingGap(null)}
            sx={dialogCloseIconSx}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingGap ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {editingGap.title}
                </Typography>
                <Typography variant="body2">
                  {formatFloatingWallClock(editingGap.gapStart, true)} –{" "}
                  {formatFloatingWallClock(editingGap.gapEnd, true)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Gap uses 15-minute steps (0–8 hours). Appointment start time
                does not change; the next service step moves with the gap.
              </Typography>
              <Stack alignItems="center" spacing={1.5}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}
                >
                  {formatGapHuman(draftGapMinutes)}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <IconButton
                    size="medium"
                    aria-label="decrease gap"
                    disabled={savingGap || draftGapMinutes <= 0}
                    onClick={() => adjustGapDraft(-15)}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <IconButton
                    size="medium"
                    aria-label="increase gap"
                    disabled={savingGap || draftGapMinutes >= 480}
                    onClick={() => adjustGapDraft(15)}
                  >
                    <AddIcon />
                  </IconButton>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Quick picks
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                  justifyContent="center"
                >
                  {QUICK_GAP_CHIPS.map((p) => (
                    <Chip
                      key={p}
                      label={formatGapHuman(p)}
                      size="small"
                      variant={draftGapMinutes === p ? "filled" : "outlined"}
                      disabled={savingGap}
                      onClick={() => setGapDraftPreset(p)}
                      sx={{
                        fontWeight: 600,
                        borderRadius: CONTROL_RADIUS,
                        ...(draftGapMinutes === p
                          ? {
                              bgcolor: colors.gold,
                              color: colors.black,
                            }
                          : {
                              borderColor: colors.gold,
                              color: ink,
                            }),
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button
            onClick={() => setEditingGap(null)}
            disabled={savingGap}
            sx={secondaryBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={savingGap}
            onClick={handleSaveGap}
            startIcon={
              savingGap ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={primaryBtnSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AddDepositModal
        open={depositOpen}
        event={event}
        onClose={() => setDepositOpen(false)}
        onRecorded={async () => {
          await refreshData?.();
        }}
      />
    </Box>
  );
};

export default AppointmentView;
